import { type InjectionKey, inject, provide } from "vue";

import { type AlertType } from "shared/components/base/AlertMessage.vue";

import { useTransientValue } from "./useTransientValue";

export type Alert = {
  type: AlertType;
  message: string;
};

export type UseTransientAlertContext = ReturnType<typeof useTransientAlert>;

export const USE_TRANSIENT_ALERT_CONTEXT = Symbol(
  "useTransientAlert"
) as InjectionKey<UseTransientAlertContext>;

export function useTransientAlert(defaultTimer = 5000) {
  const {
    value: alert,
    setValue,
    setTimeout,
  } = useTransientValue<Alert>(
    {
      type: "warning",
      message: "",
    },
    defaultTimer
  );

  const showSuccess = (message: string, timer?: number) => {
    if (timer) setTimeout(timer);
    setValue({ type: "success", message });
  };

  const showWarning = (message: string, timer?: number) => {
    if (timer) setTimeout(timer);
    setValue({ type: "warning", message });
  };

  const showInfo = (message: string, timer?: number) => {
    if (timer) setTimeout(timer);
    setValue({ type: "info", message });
  };

  const context = { alert, showSuccess, showWarning, showInfo };

  provide(USE_TRANSIENT_ALERT_CONTEXT, context);

  return context;
}

export function useTransientAlertContext() {
  return inject(USE_TRANSIENT_ALERT_CONTEXT) as UseTransientAlertContext;
}
